import { gsap } from 'gsap';
import { useLayoutEffect, type RefObject } from 'react';

/**
 * Elena v3 motion layer (GSAP). Everything here is decoration on top of working UI: with
 * prefers-reduced-motion it all switches off and the page is simply there.
 */

export const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let started = false;

/** Ambient light, cursor spotlight and magnetic buttons, once per page load. */
export function initMotion(): void {
  if (started || typeof document === 'undefined') return;
  started = true;

  const ambient = document.createElement('div');
  ambient.className = 'fx-ambient';
  ambient.setAttribute('aria-hidden', 'true');
  ambient.innerHTML =
    '<i class="fx-blob a"></i><i class="fx-blob b"></i><i class="fx-blob c"></i><i class="fx-grain"></i>';
  document.body.prepend(ambient);

  if (reducedMotion()) return;

  // Slow drifting light behind everything
  ambient.querySelectorAll<HTMLElement>('.fx-blob').forEach((blob, i) => {
    gsap.to(blob, {
      xPercent: gsap.utils.random(-30, 30),
      yPercent: gsap.utils.random(-25, 25),
      scale: gsap.utils.random(0.85, 1.25),
      duration: gsap.utils.random(9, 15),
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
      delay: i * 1.3,
    });
  });

  if (!window.matchMedia('(pointer: fine)').matches) return;

  // Spotlight that trails the cursor
  const spot = document.createElement('div');
  spot.className = 'fx-spot';
  spot.setAttribute('aria-hidden', 'true');
  document.body.appendChild(spot);
  const sx = gsap.quickTo(spot, 'x', { duration: 0.6, ease: 'power3' });
  const sy = gsap.quickTo(spot, 'y', { duration: 0.6, ease: 'power3' });
  window.addEventListener(
    'pointermove',
    (e) => {
      sx(e.clientX);
      sy(e.clientY);
    },
    { passive: true },
  );

  // Magnetic buttons: they lean toward the cursor and snap back with a little spring
  const MAGNETIC = '.mic-btn, .btn.primary, .chip, .side-link, .tab, .fx-magnetic';
  let active: HTMLElement | null = null;
  document.addEventListener(
    'pointermove',
    (e) => {
      const el = (e.target as Element | null)?.closest<HTMLElement>(MAGNETIC) ?? null;
      if (active && active !== el) {
        gsap.to(active, { x: 0, y: 0, duration: 0.7, ease: 'elastic.out(1, 0.35)' });
      }
      active = el;
      if (!el || (el as HTMLButtonElement).disabled) return;
      const r = el.getBoundingClientRect();
      const strength = el.classList.contains('side-link') ? 0.08 : 0.28;
      gsap.to(el, {
        x: (e.clientX - r.left - r.width / 2) * strength,
        y: (e.clientY - r.top - r.height / 2) * strength,
        duration: 0.35,
        ease: 'power3.out',
      });
    },
    { passive: true },
  );
  document.addEventListener(
    'pointerdown',
    (e) => {
      const el = (e.target as Element | null)?.closest<HTMLElement>(MAGNETIC);
      if (el) gsap.fromTo(el, { scale: 0.94 }, { scale: 1, duration: 0.5, ease: 'back.out(3)' });
    },
    { passive: true },
  );
}

/** Page entrance: cards and sections rise out of blur in a quick cascade. */
export function usePageReveal(ref: RefObject<HTMLElement | null>, key: unknown): void {
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root || reducedMotion()) return;
    const ctx = gsap.context(() => {
      const items = root.querySelectorAll(
        '.page > *, .page .card, .page .home-side > *, .page .grid > *',
      );
      gsap.fromTo(
        items,
        { autoAlpha: 0, y: 34, filter: 'blur(10px)', scale: 0.985 },
        {
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          scale: 1,
          duration: 0.8,
          ease: 'expo.out',
          stagger: { each: 0.045, from: 'start' },
          clearProps: 'filter,transform,opacity,visibility',
        },
      );
    }, root);
    return () => ctx.revert();
  }, [key]);
}

/** Splits a heading into letters that fall into place, then shimmer once. */
export function useLetterIntro(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    const text = el.textContent ?? '';
    el.setAttribute('aria-label', text);
    el.innerHTML = [...text]
      .map(
        (ch) => `<span class="fx-letter" aria-hidden="true">${ch === ' ' ? '&nbsp;' : ch}</span>`,
      )
      .join('');
    const letters = el.querySelectorAll('.fx-letter');
    const tl = gsap.timeline();
    tl.fromTo(
      letters,
      { yPercent: 120, rotateX: -90, autoAlpha: 0, filter: 'blur(12px)' },
      {
        yPercent: 0,
        rotateX: 0,
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 1.1,
        ease: 'expo.out',
        stagger: 0.07,
      },
    ).fromTo(
      letters,
      { textShadow: '0 0 0px rgba(97,223,255,0)' },
      {
        textShadow: '0 0 28px rgba(97,223,255,0.9)',
        duration: 0.35,
        stagger: 0.05,
        yoyo: true,
        repeat: 1,
        ease: 'sine.inOut',
      },
      '-=0.4',
    );
    return () => {
      tl.kill();
      el.textContent = text;
      el.removeAttribute('aria-label');
    };
  }, []);
}

/** Elena's reply appears word by word, like she's saying it. */
export function useWordReveal(ref: RefObject<HTMLElement | null>, text: string | undefined): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !text || reducedMotion()) return;
    const words = el.querySelectorAll('.fx-word');
    const tween = gsap.fromTo(
      words,
      { autoAlpha: 0, y: 14, filter: 'blur(6px)' },
      {
        autoAlpha: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.55,
        ease: 'power3.out',
        stagger: Math.min(0.06, 1.6 / Math.max(1, words.length)),
        clearProps: 'filter,transform',
      },
    );
    return () => {
      tween.kill();
    };
  }, [text]);
}

/** A dialog or stage that opens from a soft zoom and blur. */
export function useOpenAnimation(ref: RefObject<HTMLElement | null>, open = true): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    const tl = gsap
      .timeline()
      .fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.35, ease: 'power2.out' })
      .fromTo(
        el.firstElementChild,
        { scale: 0.9, y: 30, filter: 'blur(14px)' },
        {
          scale: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: 'expo.out',
          clearProps: 'filter,transform',
        },
        '<',
      );
    return () => {
      tl.kill();
    };
  }, [open]);
}
